import { useCallback, useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import Loading from "../../components/Loading";
import { postsService } from "../../../../services/api/postsService";
import { getCleanFileName } from "../../../../utils/fileUtils";
import PostPopup from "./PostPopup";
import { ReactComponent as LeftArrowIcons } from "../../../../assets/icons/left-arrow.svg";

const PostsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ name: "", txtUrl: "", fileUrl: "" });
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);

  const fetchPosts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await postsService.get_by_id(id, token);
      const payload = response.data;

      let txtUrl = payload.txtUrl || "";
      let fileUrl = payload.fileUrl || "";
      if (txtUrl && !fileUrl && !txtUrl.trim().startsWith("<")) {
        fileUrl = txtUrl;
        txtUrl = "";
      }

      setData({
        name: payload.name || "",
        txtUrl: txtUrl,
        fileUrl: fileUrl,
      });
    } catch (error) {
      toast.error(error.response?.data?.message || "Không thể tải dữ liệu bài viết");
      console.error("Error fetching post detail:", error);
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  if (loading && !isEditPopupOpen) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loading />
      </div>
    );
  }


  const isPdf = (url) => {
    if (!url) return false;
    return url.split("?")[0].toLowerCase().endsWith(".pdf");
  };

  return (
    <div className="h-full overflow-auto bg-gray-50/50">
      <div className="mx-auto bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden min-h-full flex flex-col relative">
        <div className="px-8 py-6 border-b border-gray-100 bg-white sticky top-0 z-10 flex justify-between items-center group">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-[#0457C2] hover:bg-blue-50 rounded-xl transition-all font-Sf-medium shrink-0"
          >
            <LeftArrowIcons />
            Quay lại
          </button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold text-[#0457C2] text-center uppercase tracking-tight">
              {data.name}
            </h1>
          </div>
          <button
            onClick={() => setIsEditPopupOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-[#0457C2] rounded-xl hover:bg-blue-100 transition-all font-Sf-medium border border-blue-100 shadow-sm shrink-0"
          >
            <img src="/icons/edit.svg" alt="edit" className="w-4 h-4" />
            Chỉnh sửa
          </button>
        </div>

        <div className="flex-1 px-8 py-8 md:px-12">
          {data.txtUrl && data.txtUrl !== "<p><br></p>" ? (
            <div
              className="prose max-w-none prose-blue prose-img:rounded-2xl prose-img:shadow-md prose-headings:text-[#1a1a1a] prose-p:text-gray-700 prose-p:leading-relaxed text-lg"
              dangerouslySetInnerHTML={{ __html: data.txtUrl }}
            />
          ) : (
            !data.fileUrl && (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <img src="/icons/document-gray.svg" alt="empty" className="w-16 h-16 opacity-20 mb-4" />
                <p>Bài viết chưa có nội dung chi tiết.</p>
              </div>
            )
          )}

          {data.fileUrl && (
            <div className={`mt-10 pt-8 border-t border-gray-100 ${!data.txtUrl ? "mt-0 pt-0 border-t-0" : ""}`}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <img src="/icons/folder.svg" alt="attach" className="w-5 h-5" />
                Dành cho bạn (File đính kèm)
              </h3>

              <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 group hover:border-blue-300 transition-all">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-center gap-4 overflow-hidden">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <img
                        src={isPdf(data.fileUrl) ? "/icons/pdf.svg" : "/icons/file.svg"}
                        alt="file type"
                        className="w-7 h-7"
                      />
                    </div>
                    <div className="overflow-hidden">
                      <p className="font-medium text-gray-900 truncate" title={getCleanFileName(data.fileUrl)}>
                        {getCleanFileName(data.fileUrl)}
                      </p>
                      <p className="text-sm text-gray-500">Nhấp để xem hoặc tải về máy</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <a
                      href={data.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-5 py-2.5 bg-[#0457C2] text-white rounded-lg font-Sf-medium hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2 whitespace-nowrap"
                    >
                      {/* <img src="/icons/eye-white.svg" alt="view" className="w-4 h-4" /> */}
                      Xem tài liệu
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-8 py-4 bg-gray-50 border-t border-gray-100 text-sm text-gray-500 flex justify-between items-center">
          <span>Tài liệu nội bộ</span>
          <span>&copy; {new Date().getFullYear()} Công ty SHTT</span>
        </div>
      </div>

      {isEditPopupOpen && (
        <PostPopup
          mode="edit"
          post_id={id}
          closePopup={() => setIsEditPopupOpen(false)}
          onSuccess={() => {
            setIsEditPopupOpen(false);
            fetchPosts();
          }}
        />
      )}
    </div>
  );
};

export default PostsDetail;
